/**
 * Bardin Roofing duck logo — uses the actual duck2.png with CSS mask
 * for coloring and progress fill. Default color: #ff9e7a (bardin-peach).
 */

import type { CSSProperties } from "react";

interface DuckIconProps {
  size?: number;
  color?: string;
  className?: string;
  /** 0-1 fill percentage for progress effect (0 = outline only, 1 = fully filled) */
  fillPercent?: number;
  /** When true, renders as outline with partial fill from bottom */
  progressMode?: boolean;
  outlineColor?: string;
}

const DUCK_IMG = "/img/duck2.png";

/** Shared mask styles — the PNG silhouette acts as the mask shape */
function maskStyle(sz: number): CSSProperties {
  return {
    display: "inline-block",
    width: sz,
    height: sz,
    WebkitMaskImage: `url(${DUCK_IMG})`,
    WebkitMaskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskImage: `url(${DUCK_IMG})`,
    maskSize: "contain",
    maskRepeat: "no-repeat",
    maskPosition: "center",
  } as CSSProperties;
}

export default function DuckIcon({
  size = 32,
  color = "#ff9e7a",
  className = "",
  fillPercent,
  progressMode = false,
  outlineColor = "#e2e8f0",
}: DuckIconProps) {
  // Simple mode — solid color duck
  if (!progressMode || fillPercent === undefined) {
    return (
      <span
        className={className}
        style={{ ...maskStyle(size), backgroundColor: color }}
        aria-hidden
      />
    );
  }

  // Progress mode — base color with fill from bottom up
  const pct = Math.max(0, Math.min(1, fillPercent));

  return (
    <span
      className={`relative ${className}`}
      style={{ display: "inline-block", width: size, height: size, flexShrink: 0 }}
      aria-hidden
    >
      {/* Base (unfilled) — full duck in outline color */}
      <span style={{ ...maskStyle(size), backgroundColor: outlineColor, position: "absolute", inset: 0 }} />

      {/* Fill overlay — clips from bottom up */}
      <span
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: `${pct * 100}%`,
          overflow: "hidden",
        }}
      >
        <span
          style={{
            ...maskStyle(size),
            backgroundColor: color,
            position: "absolute",
            bottom: 0,
            left: 0,
          }}
        />
      </span>
    </span>
  );
}
